import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, X, Sparkles, Rocket, Save, AlertCircle, Bus, CreditCard, Users, Check } from "lucide-react";
import useIsMobile from "@/hooks/useIsMobile";
import { DEFAULT_WEDDING } from "@/lib/wedding";
import { EDITOR } from "@/constants/testIds";
import { validateRequired, hasErrors, FIELD_TO_SECTION } from "@/lib/validation";
import WeddingPreview from "@/components/WeddingPreview";
import MobileSectionCarousel from "@/components/MobileSectionCarousel";
import { getEditorSections } from "@/components/editorSections";
import PricingSheet from "@/components/PricingSheet";
import { adminApi, startGoogleLogin } from "@/lib/admin";
import { BrandLogo, BrandIcon } from "@/components/Brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_KEY = "wedding_draft_v2";
const AUTOSAVE_DEBOUNCE_MS = 30_000; // 30 s de inactividad

function cleanVenue(v) {
    return {
        name: v?.name || "",
        address: v?.address || "",
        map_url: v?.map_url || "",
        time: v?.time || "",
    };
}

// Fields we actually persist. Centralised so handlePublishClick,
// handleDraftClick and the autosave effect all send the same shape.
function buildWeddingPayload(data) {
    return {
        primary_name: data.primary_name || "",
        secondary_name: data.secondary_name || "",
        wedding_date: data.wedding_date || "",
        tagline: data.tagline || "",
        message: data.message || "",
        template: data.template || "classic-elegant",
        cover_image: data.cover_image || "",
        ceremony: cleanVenue(data.ceremony),
        party: cleanVenue(data.party),
        iban: data.iban || "",
        ibanEnabled: !!data.ibanEnabled,
        busEnabled: !!data.busEnabled,
        busDeparture: data.busDeparture || "",
        busReturn: data.busReturn || "",
        countdownEnabled: data.countdownEnabled ?? true,
        messageEnabled: data.messageEnabled ?? true,
    };
}

function payloadSignature(data) {
    return JSON.stringify(buildWeddingPayload(data));
}


function loadDraft() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return DEFAULT_WEDDING;
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_WEDDING, ...parsed };
    } catch {
        return DEFAULT_WEDDING;
    }
}

// ─── Sección Autobús ────────────────────────────────────────────────────────
function BusSection({ data, setData }) {
    const busEnabled = data.busEnabled ?? false;

    const toggle = () =>
        setData((prev) => ({ ...prev, busEnabled: !prev.busEnabled }));

    const handleField = (field) => (e) =>
        setData((prev) => ({ ...prev, [field]: e.target.value }));

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Bus size={16} className="text-[var(--ink-soft)]" />
                    <span className="text-sm font-medium">
                        Mostrar información de autobús
                    </span>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={busEnabled}
                    onClick={toggle}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200"
                    style={{
                        background: busEnabled ? "var(--ink)" : "var(--line)",
                    }}
                >
                    <span
                        className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200"
                        style={{
                            transform: busEnabled ? "translateX(22px)" : "translateX(4px)",
                        }}
                    />
                </button>
            </div>

            {busEnabled && (
                <div className="space-y-4 pt-4 border-t border-[var(--line)]">
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-1.5">
                            Lugar de salida y horario de ida
                        </label>
                        <textarea
                            rows={2}
                            placeholder="Ej: Salida desde Plaza Mayor a las 12:30 h"
                            value={data.busDeparture ?? ""}
                            onChange={handleField("busDeparture")}
                            className="input-base w-full resize-none"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-1.5">
                            Horarios de vuelta
                        </label>
                        <textarea
                            rows={2}
                            placeholder="Ej: Salidas de vuelta a las 18:00 h y 01:00 h"
                            value={data.busReturn ?? ""}
                            onChange={handleField("busReturn")}
                            className="input-base w-full resize-none"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Sección IBAN ────────────────────────────────────────────────────────────
function IbanSection({ data, setData }) {
    const ibanEnabled = data.ibanEnabled ?? false;

    const toggle = () =>
        setData((prev) => ({ ...prev, ibanEnabled: !prev.ibanEnabled }));

    const handleField = (e) =>
        setData((prev) => ({ ...prev, iban: e.target.value }));

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <CreditCard size={16} className="text-[var(--ink-soft)]" />
                    <span className="text-sm font-medium">
                        Mostrar cuenta bancaria
                    </span>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={ibanEnabled}
                    onClick={toggle}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200"
                    style={{
                        background: ibanEnabled ? "var(--ink)" : "var(--line)",
                    }}
                >
                    <span
                        className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200"
                        style={{
                            transform: ibanEnabled ? "translateX(22px)" : "translateX(4px)",
                        }}
                    />
                </button>
            </div>

            {ibanEnabled && (
                <div className="space-y-4 pt-4 border-t border-[var(--line)]">
                    <label className="block text-xs font-medium uppercase tracking-[0.12em] text-[var(--ink-soft)] mb-1.5">
                        IBAN de la cuenta
                    </label>
                    <input
                        type="text"
                        placeholder="ES00 0000 0000 0000 0000 0000"
                        value={data.iban ?? ""}
                        onChange={handleField}
                        className="input-base w-full font-mono tracking-widest"
                    />
                    <p className="text-xs text-[var(--ink-soft)] opacity-70">
                        Se mostrará como botón discreto en la invitación pública.
                    </p>
                </div>
            )}
        </div>
    );
}

// ─── Editor principal ────────────────────────────────────────────────────────
export default function Editor() {
    const isMobile = useIsMobile();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [data, setData] = useState(loadDraft);
    const [loadingToken, setLoadingToken] = useState(!!token);
    const [mobilePreview, setMobilePreview] = useState(false);
    const [pricingOpen, setPricingOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState(null);
    const [focusSection, setFocusSection] = useState(null);

    // Edit-mode: when the user comes from /admin (authenticated), we hydrate
    // from the server and switch the "Publicar" button to a PUT-based save.
    const [editMode, setEditMode] = useState({
        loading: true,
        active: false,
        savedId: null,   // wedding id for messaging only
        publicSlug: "",
    });
    const [saving, setSaving] = useState(false);

    // ── Autosave silencioso ────────────────────────────────────────────────
    // Every time `data` changes we schedule a PUT after 30 s of inactivity.
    // Guardrails:
    //   • Only fires when the session is authenticated (editMode.active).
    //   • Skips while a manual save/publish is in flight (`saving`).
    //   • Skips if the current signature matches the last saved one
    //     (no real change → no request).
    //   • Skips until the first server hydration has completed, otherwise
    //     the debounce timer starting on mount would auto-save the
    //     DEFAULT_WEDDING placeholder over the user's real doc.
    const lastSavedSigRef = useRef(null);
    const autoSaveTimerRef = useRef(null);
    const autoSaveInFlightRef = useRef(false);

    // Try to hydrate from the auth session on mount. If not authed, we
    // redirect to the Google login. Every editor session is authenticated
    // now — there's no anonymous editor mode.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const full = await adminApi.weddingFull();
                if (cancelled) return;
                // The server is the source of truth. If the server has any
                // real fields set, ignore localStorage entirely so a stale
                // draft can't overwrite the persisted data. If the wedding
                // was just created and every field is default/empty, we
                // let localStorage seed it once (helps users who designed
                // pre-login).
                const isFreshWedding =
                    !full.primary_name &&
                    !full.secondary_name &&
                    !full.wedding_date;
                if (isFreshWedding) {
                    // Merge in any anonymous localStorage draft so users
                    // who designed pre-login don't lose their work. If no
                    // draft exists, we start with a mostly-empty form so
                    // the placeholders guide them.
                    const draft = loadDraft();
                    const hasDraftWork =
                        draft.primary_name ||
                        draft.secondary_name ||
                        draft.wedding_date ||
                        draft.message;
                    if (hasDraftWork) {
                        const merged = { ...DEFAULT_WEDDING, ...draft };
                        setData(merged);
                        // If we pulled in an anonymous draft the server
                        // doesn't know about, autosave should fire on the
                        // next debounce tick so we persist the merge —
                        // hence we DON'T seed lastSavedSig here.
                    } else {
                        const merged = { ...DEFAULT_WEDDING, ...full };
                        setData(merged);
                        lastSavedSigRef.current = payloadSignature(merged);
                    }
                } else {
                    const merged = { ...DEFAULT_WEDDING, ...full };
                    setData(merged);
                    lastSavedSigRef.current = payloadSignature(merged);
                }
                setEditMode({
                    loading: false,
                    active: true,
                    savedId: full.id,
                    publicSlug: full.slug || "",
                });
            } catch (err) {
                // 401 = not authenticated → redirect to Google login.
                if (err?.response?.status === 401) {
                    startGoogleLogin("/editor");
                    return;
                }
                console.warn("editor session hydrate failed", err);
                if (!cancelled) {
                    setEditMode((p) => ({ ...p, loading: false, active: false }));
                }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ── Autosave effect ─ debounce 30 s ────────────────────────────────────
    useEffect(() => {
        if (!editMode.active) return; // needs an authenticated session
        if (saving) return;           // don't stack on top of a manual save
        if (autoSaveInFlightRef.current) return;

        const currentSig = payloadSignature(data);
        // Seed on first render after hydration if it wasn't seeded yet.
        if (lastSavedSigRef.current === null) {
            lastSavedSigRef.current = currentSig;
            return;
        }
        if (currentSig === lastSavedSigRef.current) return;

        // Reset the debounce timer on every keystroke.
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }
        autoSaveTimerRef.current = setTimeout(async () => {
            // Re-check the diff at fire time: the user might have reverted
            // to the previously-saved state during the 30 s window.
            const finalSig = payloadSignature(data);
            if (finalSig === lastSavedSigRef.current) return;

            autoSaveInFlightRef.current = true;
            try {
                const updated = await adminApi.updateWedding(
                    buildWeddingPayload(data),
                );
                lastSavedSigRef.current = payloadSignature(updated);
                setEditMode((p) => ({
                    ...p,
                    publicSlug: updated.slug || p.publicSlug,
                }));
                // Silent success — we intentionally skip the toast so
                // autosave stays invisible, per user request.
            } catch (err) {
                console.warn("autosave failed", err);
                // Only surface if the session died so the user isn't
                // typing into the void.
                if (err?.response?.status === 401) {
                    setToast({
                        type: "error",
                        msg: "Tu sesión ha expirado. Vuelve a iniciar sesión.",
                    });
                }
            } finally {
                autoSaveInFlightRef.current = false;
            }
        }, AUTOSAVE_DEBOUNCE_MS);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [data, editMode.active, saving]);

    // Cargar por token (magic link). Canonical async-fetch-on-mount pattern
    // gated by `cancelled` to prevent updates after unmount.
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setLoadingToken(true);
        (async () => {
            try {
                const res = await axios.get(`${API}/drafts/${token}`);
                if (!cancelled) {
                    setData({ ...DEFAULT_WEDDING, ...res.data });
                    setToast({
                        type: "info",
                        msg: "Borrador restaurado desde tu enlace.",
                    });
                }
            } catch (err) {
                console.warn("magic link draft restore failed", err);
                if (!cancelled) {
                    setToast({
                        type: "error",
                        msg: "No se pudo restaurar el borrador.",
                    });
                }
            } finally {
                if (!cancelled) setLoadingToken(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    // Persistir en localStorage
    useEffect(() => {
        if (loadingToken) return;
        localStorage.setItem(LS_KEY, JSON.stringify(data));
    }, [data, loadingToken]);

    // Auto-dismiss toast
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3500);
        return () => clearTimeout(t);
    }, [toast]);

    const clearError = (key) => {
        setErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    // Inyectamos las secciones de Regalos y Autobús de forma estructurada en la lista principal
    const allSections = useMemo(() => {
        const baseSections = getEditorSections(data, setData, errors, clearError);
        
        return [
            ...baseSections,
            {
                id: "iban",
                title: "Cuenta bancaria",
                subtitle: "El IBAN se mostrará protegido. Los invitados solo podrán verlo y copiarlo si pulsan el botón.",
                render: () => <IbanSection data={data} setData={setData} />
            },
            {
                id: "autobus",
                title: "Autobús y Transporte",
                subtitle: "Activa esta sección si habéis organizado horarios de traslados para vuestros invitados.",
                render: () => <BusSection data={data} setData={setData} />
            }
        ];
    }, [data, errors]);

    const handlePublishClick = async () => {
        const next = validateRequired(data);
        if (hasErrors(next)) {
            setErrors(next);
            const firstField = Object.keys(next)[0];
            const sectionId = FIELD_TO_SECTION[firstField];
            setFocusSection({ id: sectionId, t: Date.now() });
            setToast({
                type: "error",
                msg: "Faltan campos obligatorios. Los hemos marcado para ti.",
            });
            return;
        }
        // Si la boda ya está PUBLICADA (pagada): guardamos cambios en la web
        // en vivo vía PUT. No hay que volver a pagar.
        // Si NO está publicada: abrimos el PricingSheet para que la pareja
        // pague los 39 € y disparar Stripe Checkout. El PricingSheet ya se
        // encarga del PUT + POST /api/checkout/create-session + redirect.
        if (editMode.active && data.published === true) {
            setSaving(true);
            try {
                const payload = buildWeddingPayload(data);
                const updated = await adminApi.updateWedding(payload);
                setData({ ...DEFAULT_WEDDING, ...updated });
                lastSavedSigRef.current = payloadSignature(updated);
                setEditMode((p) => ({ ...p, publicSlug: updated.slug || p.publicSlug }));
                setToast({
                    type: "info",
                    msg: "Cambios guardados en tu web publicada.",
                });
            } catch (e) {
                console.error("save failed", e);
                setToast({
                    type: "error",
                    msg: "No hemos podido guardar. Inténtalo de nuevo.",
                });
            } finally {
                setSaving(false);
            }
            return;
        }
        // Boda aún no publicada → abrir Pricing Sheet → Stripe Checkout.
        setPricingOpen(true);
    };

    const handleDraftClick = async () => {
        // Guardado silencioso: PUT /api/admin/wedding con el contenido
        // actual del editor + toast. No hay modal ni email — cada usuario
        // ya está autenticado con Google y su boda vive en su cuenta.
        setSaving(true);
        try {
            const payload = buildWeddingPayload(data);
            const updated = await adminApi.updateWedding(payload);
            setData({ ...DEFAULT_WEDDING, ...updated });
            lastSavedSigRef.current = payloadSignature(updated);
            setEditMode((p) => ({
                ...p,
                publicSlug: updated.slug || p.publicSlug,
            }));
            setToast({
                type: "info",
                msg: "Borrador guardado correctamente.",
            });
        } catch (e) {
            console.error("draft save failed", e);
            setToast({
                type: "error",
                msg: "No hemos podido guardar el borrador. Inténtalo de nuevo.",
            });
        } finally {
            setSaving(false);
        }
    };

    if (loadingToken) {
        return (
            <div className="h-[100dvh] flex items-center justify-center text-[var(--ink-soft)]">
                Restaurando tu borrador...
            </div>
        );
    }

    if (isMobile) {
        return (
            <div
                data-testid={EDITOR.root}
                className="h-[100dvh] w-full flex flex-col bg-[var(--bg)]"
            >
                {!mobilePreview && (
                    <>
                        <TopBarMobile
                            onPublish={handlePublishClick}
                            onDraft={handleDraftClick}
                            editMode={editMode.active}
                            saving={saving}
                        />
                        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-6 space-y-8">
                            <MobileSectionCarousel
                                sections={allSections}
                                focusSection={focusSection}
                            />
                            <div className="h-8" />
                        </div>
                        <button
                            data-testid={EDITOR.fabPreview}
                            onClick={() => setMobilePreview(true)}
                            className="fab"
                            aria-label="Ver vista previa"
                        >
                            <Eye size={22} />
                        </button>
                    </>
                )}
                {mobilePreview && (
                    <div className="fixed inset-0 z-40 bg-[var(--bg)] tpl-anim">
                        <WeddingPreview data={data} scope="mobile-full" />
                        <button
                            data-testid={EDITOR.closePreview}
                            onClick={() => setMobilePreview(false)}
                            className="fab fab-preview-close"
                            aria-label="Cerrar vista previa"
                            style={{
                                background: "#fff",
                                color: "var(--ink)",
                                border: "1px solid var(--line)",
                            }}
                        >
                            <X size={22} />
                        </button>
                    </div>
                )}
                <PricingSheet
                    open={pricingOpen}
                    onClose={() => setPricingOpen(false)}
                    wedding={data}
                />
                <Toast toast={toast} onClose={() => setToast(null)} />
            </div>
        );
    }

    // ── Desktop ──────────────────────────────────────────────────────────────
    return (
        <div
            data-testid={EDITOR.root}
            className="h-[100dvh] w-full bg-[var(--bg)] flex flex-col"
        >
            <TopBarDesktop
                onPublish={handlePublishClick}
                onDraft={handleDraftClick}
                editMode={editMode.active}
                saving={saving}
            />
            <div
                data-testid={EDITOR.splitView}
                className="flex-1 min-h-0 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
            >
                <div className="overflow-y-auto px-10 py-8 border-r border-[var(--line)]">
                    <div className="max-w-[560px] mx-auto space-y-10">
                        {/* Secciones integradas secuencialmente */}
                        {allSections.map((sec) => (
                            <section
                                key={sec.id}
                                id={`sec-${sec.id}`}
                                ref={(el) => {
                                    if (
                                        el &&
                                        focusSection?.id === sec.id &&
                                        focusSection?.t
                                    ) {
                                        el.scrollIntoView({
                                            behavior: "smooth",
                                            block: "start",
                                        });
                                    }
                                }}
                            >
                                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-1">
                                    {sec.id === "iban" || sec.id === "autobus" ? "Extras" : sec.id}
                                </div>
                                <h2 className="font-serif text-3xl leading-tight">
                                    {sec.title}
                                </h2>
                                {sec.subtitle && (
                                    <p className="text-sm text-[var(--ink-soft)] mt-1 mb-5">
                                        {sec.subtitle}
                                    </p>
                                )}
                                <div className="card-soft p-6">
                                    {sec.render()}
                                </div>
                            </section>
                        ))}
                        <div className="h-8" />
                    </div>
                </div>

                {/* Vista previa */}
                <div className="overflow-hidden bg-[var(--bg-soft)] flex items-center justify-center relative">
                    <div className="absolute top-5 left-5 chip">
                        <Sparkles size={12} />
                        Vista previa móvil en directo
                    </div>
                    <div className="phone-frame">
                        <div className="phone-screen">
                            <div className="phone-screen-content">
                                <WeddingPreview data={data} scope="desktop" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <PricingSheet
                open={pricingOpen}
                onClose={() => setPricingOpen(false)}
                wedding={data}
            />
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
}

// ─── Top bars ────────────────────────────────────────────────────────────────
function TopBarMobile({ onPublish, onDraft, editMode, saving }) {
    return (
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)] bg-[var(--bg)]/80 backdrop-blur-md">
            <a href="/" className="flex items-center gap-2">
                <BrandIcon
                    variant="dark"
                    style={{ height: 26, width: "auto" }}
                />
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-soft)]">
                    Editor
                </div>
            </a>
            <div className="flex items-center gap-2">
                <a
                    href="/admin"
                    data-testid="editor-nav-admin"
                    className="btn-ghost"
                    style={{ padding: "9px 12px", fontSize: 12 }}
                    aria-label="Panel de invitados"
                >
                    <Users size={14} />
                </a>
                <button
                    data-testid={EDITOR.draftButton}
                    onClick={onDraft}
                    className="btn-ghost"
                    style={{ padding: "9px 12px", fontSize: 12 }}
                    aria-label="Guardar borrador"
                >
                    <Save size={14} />
                </button>
                <button
                    data-testid={EDITOR.publishButton}
                    onClick={onPublish}
                    disabled={saving}
                    className="btn-primary"
                    style={{ padding: "10px 16px", fontSize: 13 }}
                >
                    {saving ? <Check size={14} /> : <Rocket size={14} />}
                    {editMode ? (saving ? "Guardando..." : "Guardar") : "Publicar"}
                </button>
            </div>
        </div>
    );
}

function TopBarDesktop({ onPublish, onDraft, editMode, saving }) {
    return (
        <div className="flex items-center justify-between px-10 py-5 border-b border-[var(--line)]">
            <a
                href="/"
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
                <BrandIcon
                    variant="dark"
                    style={{ height: 32, width: "auto" }}
                />
                <BrandLogo
                    variant="dark"
                    style={{ height: 15, width: "auto" }}
                />
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--ink-soft)] border-l border-[var(--line)] pl-3">
                    Editor
                </div>
            </a>
            <div className="flex items-center gap-3">
                <a
                    href="/admin"
                    data-testid="editor-nav-admin"
                    className="btn-ghost"
                >
                    <Users size={14} />
                    Panel de invitados
                </a>
                <button
                    data-testid={EDITOR.draftButton}
                    onClick={onDraft}
                    className="btn-ghost"
                >
                    <Save size={14} />
                    Guardar borrador
                </button>
                <button
                    data-testid={EDITOR.publishButton}
                    onClick={onPublish}
                    className="btn-primary"
                >
                    <Rocket size={14} />
                    Publicar mi web
                </button>
            </div>
        </div>
    );
}

// ─── Toast ───────────────────────────────────────────────────────────────────
function Toast({ toast, onClose }) {
    if (!toast) return null;
    const isError = toast.type === "error";
    return (
        <div
            data-testid={EDITOR.validationToast}
            className="fixed left-1/2 -translate-x-1/2 bottom-6 md:bottom-8 z-[80] tpl-anim"
        >
            <div
                className="flex items-center gap-3 px-5 py-3 rounded-full shadow-2xl border"
                style={{
                    background: isError ? "#1a1714" : "#ffffff",
                    color: isError ? "#fff" : "var(--ink)",
                    borderColor: isError ? "transparent" : "var(--line)",
                }}
            >
                <AlertCircle
                    size={16}
                    className={isError ? "text-[#e0a787]" : ""}
                />
                <span className="text-sm">{toast.msg}</span>
                <button
                    onClick={onClose}
                    className="opacity-70 hover:opacity-100"
                    aria-label="Cerrar aviso"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}
